/**
 * The freshness badge (SPEC §9.3) — the signature element.
 *
 * "When you replace a trusted tool, the first question is always 'is this
 * current?' Make the answer impossible to miss."
 *
 * Four states, and each one is a different answer:
 *
 * | state      | when                                    | reads as                 |
 * |------------|-----------------------------------------|--------------------------|
 * | fresh      | within the report's threshold           | neutral                  |
 * | stale      | older than `freshness_warn_after_seconds` | amber                  |
 * | failed     | the freshness query itself errored      | red                      |
 * | unconfigured | the report declares no freshness query | muted, and says so       |
 * | snapshot   | a snapshot report with no source timestamp | neutral, worded differently |
 *
 * The fourth exists because §9.3 insists on it: an unknown is information, and
 * showing nothing lets a reader assume the number is current.
 *
 * The fifth exists because for a snapshot report over a view with no
 * consolidation step (§8.9), the only timestamp that exists is when this
 * application read the source. "Data as of" would claim the numbers were
 * produced then. It says "Read from source" instead, which is what happened.
 */

import { AlertTriangle, CircleHelp, Clock, DatabaseZap, RefreshCw, TriangleAlert } from 'lucide-react'

import { formatDateTime, formatRelative } from '../lib/format'
import { freshnessState } from '../lib/freshness'
import type { FreshnessBadgeProps, FreshnessState } from '../lib/freshness'

const STYLES: Record<FreshnessState, string> = {
  fresh: 'border-[var(--color-rule)] bg-[var(--color-surface-raised)] text-[var(--color-ink)]',
  stale: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  failed:
    'border-[var(--color-negative)]/40 bg-[var(--color-negative)]/10 text-[var(--color-negative)]',
  unconfigured: 'border-[var(--color-rule)] bg-transparent text-[var(--color-ink-muted)]',
  snapshot: 'border-[var(--color-rule)] bg-[var(--color-surface-raised)] text-[var(--color-ink)]',
}

const ICONS: Record<FreshnessState, typeof Clock> = {
  fresh: Clock,
  stale: AlertTriangle,
  failed: TriangleAlert,
  unconfigured: CircleHelp,
  snapshot: DatabaseZap,
}

export function FreshnessBadge({
  dataAsOf,
  error,
  warnAfterSeconds,
  now = new Date(),
  kind = null,
  snapshotTtlSeconds = null,
  onRefresh,
  refreshing = false,
}: FreshnessBadgeProps) {
  const state = freshnessState(dataAsOf, error, warnAfterSeconds, now, kind, snapshotTtlSeconds)
  const Icon = ICONS[state]

  // §9.3's exact phrasing: relative time in the badge, absolute in the tooltip.
  const absolute = dataAsOf === null ? null : formatDateTime(dataAsOf)
  const relative = dataAsOf === null ? null : formatRelative(dataAsOf, now)

  let label: string
  let title: string
  switch (state) {
    case 'failed':
      label = 'Freshness check failed'
      title = error ?? 'The freshness query failed.'
      break
    case 'unconfigured':
      label = 'Freshness not configured'
      title =
        warnAfterSeconds === null
          ? 'This report declares no freshness query, so how current its data is cannot be shown.'
          : 'The freshness query ran but the source reported no timestamp.'
      break
    case 'snapshot':
      label = `Read from source ${relative ?? ''}`
      title =
        `This application read the source ${relative ?? ''} (${absolute ?? ''}). ` +
        'The source records no timestamp of its own, so this is when the data was ' +
        'read rather than when it was produced.'
      break
    default:
      label = `Data as of ${relative ?? ''}`
      title = `Data as of ${relative ?? ''} (${absolute ?? ''})`
      if (state === 'stale') {
        title +=
          kind === 'snapshot'
            ? ' — the scheduled reload has not succeeded.'
            : ' — older than this report expects.'
      }
  }

  const stamped = state === 'fresh' || state === 'stale' || state === 'snapshot'
  const lead = state === 'snapshot' ? 'Read from source' : 'Data as of'

  return (
    <span className="inline-flex items-center gap-2">
      <span
        className={`inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border px-2 py-1 text-[length:var(--text-xs)] ${STYLES[state]}`}
        title={title}
        data-state={state}
      >
        <Icon aria-hidden className="size-3.5 shrink-0" />
        <span>
          {stamped ? (
            <>
              {lead} <span className="numeric">{relative}</span>
            </>
          ) : (
            label
          )}
        </span>
        {/* The absolute stamp is in the tooltip per §9.3, but a tooltip is not
            reachable by keyboard or screen reader, so it is also here. */}
        <span className="sr-only">{title}</span>
      </span>
      {onRefresh === undefined ? null : (
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          data-testid="refresh-snapshot"
          className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--color-rule)] px-2 py-1 text-[length:var(--text-xs)] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
          // The wait is measured in seconds against a real view, so the label
          // says what is happening rather than leaving a dead-looking button.
          title="Re-read this report's data from its source. This can take several seconds."
        >
          <RefreshCw aria-hidden className={`size-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Reading source…' : 'Refresh data'}
        </button>
      )}
    </span>
  )
}
